/**
 * E-056 follow-on — dead-letter age/repeat signal. A destination failing
 * identically for weeks should be louder than a history row: this banner
 * sits at the top of Export History and names the streak — e.g.
 * "DownstreamSQL — failing identically for 22 days (14 attempts, same error)".
 *
 * Loudness threshold (deliberate): shown when the streak has repeated
 * (>= 2 attempts) OR its first failure is >= 24h old. A single fresh
 * failure stays quiet — the retry pipeline is still working on it.
 */
import styles from "./DestinationHealthBanner.module.css";

export interface DestinationHealth {
  destinationId: string;
  destinationName: string;
  adapterType: string;
  /** Consecutive most-recent failures sharing one error signature. */
  streakCount: number;
  firstFailingAt: string;
  lastFailingAt: string;
  lastError: string | null;
  /** True when the streak ran off the server's scan window (it may be longer). */
  windowClamped: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

function isLoud(d: DestinationHealth, now: number): boolean {
  return d.streakCount >= 2 || now - new Date(d.firstFailingAt).getTime() >= DAY_MS;
}

function formatAge(ms: number): string {
  if (ms >= DAY_MS) {
    const days = Math.round(ms / DAY_MS);
    return `${days} day${days === 1 ? "" : "s"}`;
  }
  const hours = Math.max(1, Math.round(ms / HOUR_MS));
  return `${hours} hour${hours === 1 ? "" : "s"}`;
}

function formatStreak(d: DestinationHealth, now: number): string {
  const age = formatAge(now - new Date(d.firstFailingAt).getTime());
  const attempts = `${d.streakCount}${d.windowClamped ? "+" : ""} attempt${
    d.streakCount === 1 && !d.windowClamped ? "" : "s"
  }`;
  return d.streakCount >= 2
    ? `failing identically for ${age} (${attempts}, same error)`
    : `failing for ${age} (${attempts})`;
}

export function DestinationHealthBanner({
  destinations,
  onSelectDestination,
}: {
  destinations: DestinationHealth[];
  onSelectDestination: (destinationId: string) => void;
}) {
  const now = Date.now();
  const loud = destinations.filter((d) => isLoud(d, now));
  if (loud.length === 0) return null;

  return (
    <div className={styles.stack} role="alert" data-testid="destination-health-banner">
      {loud.map((d) => (
        <div key={d.destinationId} className={styles.banner}>
          <div className={styles.content}>
            <span className={styles.headline}>
              <span className={styles.name}>{d.destinationName}</span>
              {" — "}
              {formatStreak(d, now)}
            </span>
            {d.lastError && (
              <span className={styles.error} title={d.lastError}>
                {d.lastError}
              </span>
            )}
          </div>
          <button
            type="button"
            className="rb-btn"
            onClick={() => onSelectDestination(d.destinationId)}
          >
            View history
          </button>
        </div>
      ))}
    </div>
  );
}
